"use client";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { useSession } from "next-auth/react";
import apiFetch from "@/lib/fetch";
import isPhoneValid from "@/lib/is-phone-valid";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import UserIcon from "@heroicons/react/24/solid/UserIcon";
import SwatchIcon from "@heroicons/react/24/outline/SwatchIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import QuoteCustomAutoComplete from "@/components/views/quote-venue/steps/select-screen/custom-autocomplete";
import { GetQuoteSelectScreenProvider } from "@/context/quote-venue/GetQuoteSelectContext";
import { useGetQuote } from "@/context/quote-venue/GetQuoteData";
import { Input, InputMask } from "@/components/views/add-place/Input";
import QuoteUsageTerms from "../UsageTerms";
import { useTranslation } from "@/hooks/translationContext";

interface Props {
  setValidStep: (state: boolean) => void;
  setErrorMessage: (state: string) => void;
  userData: any;
  setUserData: any;
}

const mapApiData = (data: any[]) =>
  data.map((d: any) => ({
    id: d.id,
    title: d.title || d.descriptions?.[0]?.title,
    venueCount: d.venueCount,
  }));

const useCustomApiData = ({
  route,
  query,
  skip,
}: {
  route: string;
  query: string;
  skip: boolean;
}) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (skip) return;
    const fetchData = async () => {
      try {
        const res = await apiFetch({ route: `${route}?q=${query}` });
        setData(mapApiData(res.data || []));
      } catch (err) {
        console.error("CombinedInitialScreen fetch error", err);
      }
    };
    fetchData();
  }, [route, query, skip]);

  return { data };
};

export default function CombinedInitialScreen({
  setValidStep,
  setErrorMessage,
  userData,
  setUserData,
}: Props) {
  const { t } = useTranslation();
  const { data, onChange } = useGetQuote();
  const { status: sessionStatus } = useSession();

  const [serviceQuery, setServiceQuery] = useState("");
  const [districtQuery, setDistrictQuery] = useState("");
  const [debouncedServiceQuery] = useDebounce(serviceQuery, 500);
  const [debouncedDistrictQuery] = useDebounce(districtQuery, 500);

  const [districtInputIsFocused, setDistrictInputIsFocused] = useState(false);
  const [serviceInputIsFocused, setServiceInputIsFocused] = useState(false);

  const [formattedPhone, setFormattedPhone] = useState("");

  const { data: districts } = useCustomApiData({
    route: `district/active-district/${debouncedDistrictQuery ? "search" : "all"}`,
    query: debouncedDistrictQuery || "",
    skip: false,
  });

  const { data: services } = useCustomApiData({
    route: data?.selectedDistrict?.id
      ? `service/by-district/${data?.selectedDistrict?.id}/${debouncedServiceQuery ? "search" : "all"}`
      : debouncedServiceQuery
        ? "service/search"
        : "service/all",
    query: debouncedServiceQuery || "",
    skip: false,
  });

  const [preferredServices, setPreferredServices] = useState<any[]>([]);
  const [showAllServices, setShowAllServices] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setShowAllServices(false);
    const fetchPreferred = async () => {
      try {
        const districtId = data?.selectedDistrict?.id;
        const route = districtId
          ? `service/preferred/${districtId}`
          : `service/preferred`;
        const res = await apiFetch({ route });
        if (!isCurrent) return;
        const arr = Array.isArray(res) ? res : (res?.data ?? []);
        setPreferredServices(mapApiData(arr));
      } catch (err) {
        if (!isCurrent) return;
        console.error("CombinedInitialScreen preferred fetch error", err);
        setPreferredServices([]);
      }
    };
    fetchPreferred();
    return () => {
      isCurrent = false;
    };
  }, [data?.selectedDistrict?.id]);

  useEffect(() => {
    if (!serviceQuery) setShowAllServices(false);
  }, [serviceQuery]);

  const showPreferred =
    !showAllServices &&
    !debouncedServiceQuery &&
    preferredServices.length > 0;
  const displayedServices = showPreferred ? preferredServices : services;

  const serviceFooter = showPreferred ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowAllServices(true);
      }}
      className="py-3 w-full flex items-center justify-between border-t border-gray-200"
    >
      <div className="flex items-center gap-2 text-sm text-primary font-bold">
        <SwatchIcon className="size-4" />
        <span>Başka Bir Hizmet Aramak İstiyorum</span>
      </div>
      <ChevronRightIcon className="text-primary size-4" />
    </button>
  ) : null;

  useEffect(() => {
    if (data?.selectedDistrict) setDistrictQuery(data?.selectedDistrict.title);
    if (data?.selectedService) setServiceQuery(data?.selectedService.title);
  }, [data?.selectedDistrict, data?.selectedService]);

  const validDistrict = !!data?.selectedDistrict?.id;
  const validService = !!data?.selectedService?.id;
  const validName = userData?.name?.length > 3 && userData?.name?.length < 64;
  const validPhone = isPhoneValid(userData?.phone);
  const isAgreed = userData?.isAgreed === true;

  useEffect(() => {
    if (!validDistrict) {
      setErrorMessage(t("quote-page-district-required-warning"));
      setValidStep(false);
      return;
    }
    if (!validService) {
      setErrorMessage(t("quote-page-service-required-warning"));
      setValidStep(false);
      return;
    }
    if (!validName) {
      setErrorMessage(t("quote-page-fullname-min-length-warning"));
      setValidStep(false);
      return;
    }
    if (!validPhone) {
      setErrorMessage(t("quote-page-phone-invalid-warning"));
      setValidStep(false);
      return;
    }
    if (!isAgreed) {
      setErrorMessage(t("quote-page-terms-must-accept-warning"));
      setValidStep(false);
      return;
    }
    setErrorMessage(null);
    setValidStep(true);
  }, [validDistrict, validService, validName, validPhone, isAgreed]);

  const districtHandleFocus = (event: any) => {
    if (districtInputIsFocused) {
      setDistrictInputIsFocused(false);
      setServiceInputIsFocused(false);
      event.target?.blur();
    } else {
      setDistrictInputIsFocused(true);
      setServiceInputIsFocused(false);
    }
  };

  const serviceHandleFocus = (event: any) => {
    if (serviceInputIsFocused) {
      setServiceInputIsFocused(false);
      setDistrictInputIsFocused(false);
      event.target?.blur();
    } else {
      setServiceInputIsFocused(true);
      setDistrictInputIsFocused(false);
    }
  };

  const formatPhoneForSave = (phone: string) => {
    const cleaned = phone.replace(/\s+/g, "");
    return cleaned.startsWith("+90") ? cleaned : `+90${cleaned}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneWithSpaces = e.target.value;
    if (phoneWithSpaces.startsWith("0")) {
      phoneWithSpaces = phoneWithSpaces.slice(1);
    }
    setFormattedPhone(phoneWithSpaces);
    setUserData({ ...userData, phone: formatPhoneForSave(phoneWithSpaces) });
  };

  return (
    <GetQuoteSelectScreenProvider
      districts={districts}
      services={displayedServices}
      serviceQuery={serviceQuery}
      districtQuery={districtQuery}
      districtInputIsFocused={districtInputIsFocused}
      serviceInputIsFocused={serviceInputIsFocused}
      setDistrictInputIsFocused={setDistrictInputIsFocused}
      setServiceInputIsFocused={setServiceInputIsFocused}
      setDistrictQuery={setDistrictQuery}
      setServiceQuery={setServiceQuery}
    >
      <div className="w-full flex flex-col gap-5 md:pb-6">
        <QuoteCustomAutoComplete
          id="quote-district-select-input"
          parentId="quote-district-select-container"
          label={t("header-quicksearch-district")}
          handleFocus={districtHandleFocus}
          data={districts}
          inputIsFocused={districtInputIsFocused}
          setInputIsFocused={setDistrictInputIsFocused}
          query={districtQuery}
          setQuery={setDistrictQuery}
          selectedData={data?.selectedDistrict}
          setSelectedData={(value) =>
            onChange("data", "selectedDistrict", value)
          }
        />

        <QuoteCustomAutoComplete
          id="quote-service-select-input"
          parentId="quote-service-select-container"
          label={t("header-quicksearch-service")}
          handleFocus={serviceHandleFocus}
          data={displayedServices}
          inputIsFocused={serviceInputIsFocused}
          setInputIsFocused={setServiceInputIsFocused}
          query={serviceQuery}
          setQuery={setServiceQuery}
          selectedData={data?.selectedService}
          setSelectedData={(value) =>
            onChange("data", "selectedService", value)
          }
          footer={serviceFooter}
        />

        <Input
          disabled={sessionStatus == "authenticated"}
          className="shadow border border-slate-300"
          value={userData?.name || ""}
          error={userData?.name?.length > 0 && userData?.name?.length < 3}
          onChange={(e: ReactEvents.ChangeEvent<HTMLInputElement>) =>
            setUserData({ ...userData, name: e.target.value })
          }
          placeholder="Adınız"
          id="name"
          icon={<UserIcon className="size-5" />}
        />

        <InputMask
          helperText={t("quote-page-phone-hint")}
          maskOptions={{
            mask: "___ ___ __ __",
            replacement: { _: /\d/ },
          }}
          type="tel"
          className="shadow border border-slate-300"
          value={formattedPhone}
          error={formattedPhone.length > 0 && !validPhone}
          onChange={handlePhoneChange}
          placeholder="Telefon Numaranız"
          id="phone"
          icon={<PhoneIcon className="size-5" />}
        />

        <QuoteUsageTerms />
      </div>
    </GetQuoteSelectScreenProvider>
  );
}
